import { useState } from 'react';
import { unitService } from 'services/api';
import styles from './PropertyDetailModal.module.css';

const UNITS_PER_BUILDING_LIMIT = 200;

// Modal "Ver propiedad" — solo lectura. Las torres arrancan colapsadas; las
// unidades de cada una se cargan bajo demanda al expandir (PERF-03: una
// propiedad grande puede tener miles de unidades, traerlas todas de una vez
// de entrada es lento e innecesario si el usuario nunca abre esa torre).
export default function PropertyDetailModal({ property, data, loading, onClose }) {
  const [expanded,      setExpanded]      = useState({});
  const [buildingUnits, setBuildingUnits] = useState({}); // { [buildingId]: {loading, data, total} }

  const toggleBuilding = (buildingId) => {
    const willOpen = !expanded[buildingId];
    setExpanded(e => ({ ...e, [buildingId]: willOpen }));
    if (willOpen && !buildingUnits[buildingId]) {
      setBuildingUnits(bu => ({ ...bu, [buildingId]: { loading: true, data: [], total: 0 } }));
      unitService.getAll({ building_id: buildingId, limit: UNITS_PER_BUILDING_LIMIT })
        .then(r => {
          const body = r.data?.data ? r.data : { data: r.data || [], total: (r.data || []).length };
          setBuildingUnits(bu => ({ ...bu, [buildingId]: { loading: false, data: body.data, total: body.total } }));
        })
        .catch(() => setBuildingUnits(bu => ({ ...bu, [buildingId]: { loading: false, data: [], total: 0, error: true } })));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${styles.modalDetalle}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3><i className={`fas fa-building ${styles.iconoTitulo}`} />{property.name}</h3>
            <div className={styles.subtitulo}>
              {property.address}{property.city ? ` · ${property.city}` : ''}
            </div>
            {property.phone && (
              <div className={styles.subtitulo}>
                <i className={`fas fa-phone ${styles.iconoTelefono}`} />{property.phone}
              </div>
            )}
          </div>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        <div className={`modal-body ${styles.cuerpo}`}>
          {loading ? (
            <div className="loading"><div className="spinner" /></div>
          ) : !data ? (
            <div className="empty-state"><i className="fas fa-building" /><p>No se pudo cargar la información</p></div>
          ) : !data.buildings || data.buildings.length === 0 ? (
            <div className="empty-state"><i className="fas fa-layer-group" /><p>Esta propiedad no tiene torres registradas</p></div>
          ) : (
            <div className={styles.listaTorres}>
              {data.buildings.map(b => {
                const isOpen = !!expanded[b.id];
                const bu = buildingUnits[b.id];
                const units = bu?.data || [];
                // Antes de expandir (o mientras carga) se muestra el conteo
                // que ya trajo el listado de torres (units_count, barato);
                // una vez cargada, el conteo real de la torre.
                const displayCount = bu ? bu.total : b.units_count;
                return (
                  <div key={b.id} className={styles.torre}>
                    <div
                      onClick={() => toggleBuilding(b.id)}
                      className={styles.torreCabecera}
                    >
                      <div className={styles.torreTitulo}>
                        <i className={`fas fa-layer-group ${styles.torreIcono}`} />
                        <span className={styles.torreNombre}>{b.numero_torre}</span>
                        <span className={styles.torreConteo}>· {displayCount} unidad{displayCount !== 1 ? 'es' : ''}</span>
                      </div>
                      <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} ${styles.torreFlecha}`} />
                    </div>

                    {isOpen && (
                      bu?.loading ? (
                        <div className={`loading ${styles.cargandoUnidades}`}><div className="spinner" /></div>
                      ) : bu?.error ? (
                        <div className={styles.errorUnidades}>
                          Error al cargar las unidades de esta torre
                        </div>
                      ) : units.length === 0 ? (
                        <div className={styles.sinUnidades}>
                          Sin unidades registradas en esta torre
                        </div>
                      ) : (
                        <>
                        <table className={styles.tabla}>
                          <thead>
                            <tr className={styles.filaTitulos}>
                              <th className={styles.th}>Unidad</th>
                              <th className={styles.th}>Tipo</th>
                              <th className={styles.thDerecha}>Coeficiente</th>
                            </tr>
                          </thead>
                          <tbody>
                            {units.map((u, idx) => (
                              <tr key={u.id} className={idx % 2 === 0 ? styles.filaPar : styles.filaImpar}>
                                <td className={styles.celdaUnidad}>{u.numero_unidad}</td>
                                <td className={styles.celdaTipo}>{u.tipo_inmueble || '—'}</td>
                                <td className={styles.celdaCoeficiente}>
                                  {parseFloat(u.coefficiente_votacion || 0).toFixed(4)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className={styles.filaSubtotal}>
                              <td colSpan={2} className={styles.subtotalTexto}>Subtotal{bu.total > units.length ? ` (primeras ${units.length})` : ''}</td>
                              <td className={styles.subtotalValor}>
                                {units.reduce((s, u) => s + parseFloat(u.coefficiente_votacion || 0), 0).toFixed(4)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                        {bu.total > units.length && (
                          <div className={styles.nota}>
                            Mostrando las primeras {units.length} de {bu.total} unidades.
                          </div>
                        )}
                        </>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className={styles.resumen}>
            {data?.buildings?.length || 0} torre{(data?.buildings?.length || 0) !== 1 ? 's' : ''} ·{' '}
            {data?.buildings?.reduce((s, b) => s + (b.units_count || 0), 0) || 0} unidades totales
          </div>
          <button className="btn btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
